import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { User } from '../../models/User';
import { UserAddress } from '../../types/address';
import { useLanguage } from '../../contexts/LanguageContext';
import { Input } from '../ui/input';

interface ProfileFormData {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  address: {
    street: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    apartment?: string;
  };
  emergencyContact: {
    name: string;
    phone: string;
    relationship: string;
    address?: {
      street: string;
      city: string;
      state: string;
      postalCode: string;
      country: string;
      apartment?: string;
    };
  };
}

export default function ProfileModal() {
  const { t } = useLanguage();
  const { currentUser, logout } = useAuth();
  const { currentStep, handleUpdateProfile } = useOnboarding();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<ProfileFormData>({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    address: {
      street: '',
      city: '',
      state: '',
      postalCode: '',
      country: '',
      apartment: '',
    },
    emergencyContact: {
      name: '',
      phone: '',
      relationship: '',
      address: {
        street: '',
        city: '',
        state: '',
        postalCode: '',
        country: '',
        apartment: '',
      },
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;

    try {
      setLoading(true);
      
      // Create user address
      const userAddress: UserAddress = {
        street: formData.address.street,
        city: formData.address.city,
        state: formData.address.state,
        postalCode: formData.address.postalCode,
        country: formData.address.country,
      };
      if (formData.address.apartment) {
        userAddress.apartment = formData.address.apartment;
      }

      // Create emergency contact address if provided
      let emergencyContactAddress: UserAddress | undefined;
      if (formData.emergencyContact.address?.street) {
        emergencyContactAddress = {
          street: formData.emergencyContact.address.street,
          city: formData.emergencyContact.address.city,
          state: formData.emergencyContact.address.state,
          postalCode: formData.emergencyContact.address.postalCode,
          country: formData.emergencyContact.address.country,
        };
        if (formData.emergencyContact.address.apartment) {
          emergencyContactAddress.apartment = formData.emergencyContact.address.apartment;
        }
      }

      const userData: Partial<User> = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phoneNumber: formData.phoneNumber,
        address: userAddress,
      };
      
      const ec = formData.emergencyContact;
      if (ec.name || ec.phone || ec.relationship || emergencyContactAddress) {
        const emergencyContact: any = {
          name: ec.name,
          phone: ec.phone,
          relationship: ec.relationship,
        };
        if (emergencyContactAddress) {
          emergencyContact.address = emergencyContactAddress;
        }
        userData.emergencyContact = emergencyContact;
      }
      
      await handleUpdateProfile(userData);
    } catch (err) {
      setError(t('onboarding.updateProfileFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name.startsWith('emergencyContact.')) {
      const parts = name.split('.');
      if (parts.length === 3) {
        // Handle emergencyContact.address.field
        const [, subsection, field] = parts;
        setFormData(prev => ({
          ...prev,
          emergencyContact: {
            ...prev.emergencyContact,
            [subsection]: {
              ...((prev.emergencyContact[subsection as keyof typeof prev.emergencyContact] as object) || {}),
              [field]: value,
            },
          },
        }));
      } else {
        // Handle emergencyContact.field
        const field = name.split('.')[1];
        setFormData(prev => ({
          ...prev,
          emergencyContact: {
            ...prev.emergencyContact,
            [field]: value,
          },
        }));
      }
    } else if (name.startsWith('address.')) {
      // Handle address.field
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [field]: value,
        },
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Only render when current step is 'profile'
  if (currentStep !== 'profile') {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
      <div className="bg-white rounded-lg p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-6">{t('onboarding.completeProfile')}</h2>
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">{t('settings.basicInformation')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  label={t('settings.firstName')}
                />
                <Input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  label={t('settings.lastName')}
                />
                <Input
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  required
                  label={t('settings.phoneNumber')}
                />
              </div>
            </div>

            {/* Address Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">{t('settings.addressInformation')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Input
                    type="text"
                    name="address.street"
                    value={formData.address.street}
                    onChange={handleChange}
                    required
                    label={t('settings.streetAddress')}
                  />
                </div>
                <Input
                  type="text"
                  name="address.apartment"
                  value={formData.address.apartment || ''}
                  onChange={handleChange}
                  label={t('settings.apartmentUnit')}
                />
                <Input
                  type="text"
                  name="address.city"
                  value={formData.address.city}
                  onChange={handleChange}
                  required
                  label={t('settings.city')}
                />
                <Input
                  type="text"
                  name="address.state"
                  value={formData.address.state}
                  onChange={handleChange}
                  required
                  label={t('settings.stateProvince')}
                />
                <Input
                  type="text"
                  name="address.postalCode"
                  value={formData.address.postalCode}
                  onChange={handleChange}
                  required
                  label={t('settings.postalCode')}
                />
                <Input
                  type="text"
                  name="address.country"
                  value={formData.address.country}
                  onChange={handleChange}
                  required
                  label={t('settings.country')}
                />
              </div>
            </div>

            {/* Emergency Contact */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">{t('members.emergencyContact')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  type="text"
                  name="emergencyContact.name"
                  value={formData.emergencyContact.name}
                  onChange={handleChange}
                  label={t('common.name')}
                />
                <Input
                  type="tel"
                  name="emergencyContact.phone"
                  value={formData.emergencyContact.phone}
                  onChange={handleChange}
                  label={t('common.phone')}
                />
                <Input
                  type="text"
                  name="emergencyContact.relationship"
                  value={formData.emergencyContact.relationship}
                  onChange={handleChange}
                  label={t('members.relationship')}
                />
              </div>

              {/* Emergency Contact Address */}
              <div className="space-y-4">
                <h4 className="text-md font-medium text-gray-700">{t('onboarding.emergencyContactAddressOptional')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Input
                      type="text"
                      name="emergencyContact.address.street"
                      value={formData.emergencyContact.address?.street || ''}
                      onChange={handleChange}
                      label={t('settings.streetAddress')}
                    />
                  </div>
                  <Input
                    type="text"
                    name="emergencyContact.address.apartment"
                    value={formData.emergencyContact.address?.apartment || ''}
                    onChange={handleChange}
                    label={t('settings.apartmentUnit')}
                  />
                  <Input
                    type="text"
                    name="emergencyContact.address.city"
                    value={formData.emergencyContact.address?.city || ''}
                    onChange={handleChange}
                    label={t('settings.city')}
                  />
                  <Input
                    type="text"
                    name="emergencyContact.address.state"
                    value={formData.emergencyContact.address?.state || ''}
                    onChange={handleChange}
                    label={t('settings.stateProvince')}
                  />
                  <Input
                    type="text"
                    name="emergencyContact.address.postalCode"
                    value={formData.emergencyContact.address?.postalCode || ''}
                    onChange={handleChange}
                    label={t('settings.postalCode')}
                  />
                  <Input
                    type="text"
                    name="emergencyContact.address.country"
                    value={formData.emergencyContact.address?.country || ''}
                    onChange={handleChange}
                    label={t('settings.country')}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="mt-6 space-y-3">
            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? t('common.saving') : t('onboarding.saveAndContinue')}
            </button>
            
            <button
              type="button"
              onClick={async () => {
                setLoading(true);
                try {
                  await logout();
                } finally {
                  setLoading(false);
                  // Modal will close due to auth context change
                }
              }}
              className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              {t('common.logout')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 